import { useAuth } from "react-oidc-context";
import { ExternalLink, FlaskConical, LogOut, ShieldOff } from "lucide-react";
import type { AccesoAmbiente } from "@/lib/ambiente";
import { ETIQUETA_AMBIENTE, URL_PRODUCCION } from "@/lib/ambiente";
import { BandaAmbiente } from "@/components/BandaAmbiente";

/**
 * Pantalla que ve un usuario interno de NPMX cuando el ambiente de pruebas no
 * le está abierto. Sustituye a toda la aplicación: no hay menú ni datos detrás
 * que puedan confundirse con el sistema real. Las únicas salidas son ir a
 * producción o cerrar la sesión.
 */
export function AccesoAmbienteBloqueado({ acceso }: { acceso: AccesoAmbiente | null }) {
  const auth = useAuth();
  const etiqueta = acceso?.ambiente.etiqueta ?? ETIQUETA_AMBIENTE ?? "Ambiente de pruebas";
  const urlProduccion = acceso?.ambiente.urlProduccion ?? URL_PRODUCCION;
  const correo = acceso?.correo ?? auth.user?.profile?.email;

  return (
    <div className="flex min-h-screen flex-col bg-app text-fg">
      <BandaAmbiente />
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg rounded-2xl border border-edge bg-surface p-6 shadow-card sm:p-8">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              <ShieldOff className="h-6 w-6" aria-hidden="true" />
            </span>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-700/30 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
              <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
              {etiqueta}
            </div>
          </div>

          <h1 className="mt-5 text-xl font-semibold text-fg">
            Tu cuenta no tiene acceso al ambiente de pruebas
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-fg-muted">
            {acceso?.motivo ??
              "Este no es el sistema productivo de SICEN. Las cuentas internas de NPMX solo pueden entrar aquí con autorización expresa."}
          </p>
          {correo && (
            <p className="mt-2 text-xs text-fg-subtle">
              Sesión iniciada como <strong className="text-fg">{correo}</strong>.
            </p>
          )}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <a
              href={urlProduccion}
              rel="noopener"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-npmx-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-npmx-800"
            >
              Ir al SICEN productivo
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={() => auth.signoutRedirect()}
              className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-edge bg-surface px-4 py-2.5 text-sm font-semibold text-fg transition hover:bg-surface-2"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Cerrar sesión
            </button>
          </div>

          <p className="mt-5 text-xs text-fg-subtle">
            ¿Participas en las pruebas de este ambiente? Pide al administrador de SICEN que
            autorice tu cuenta.
          </p>
        </div>
      </main>
    </div>
  );
}
